/* One deadline includes headers AND body consumption; mutations are never retried. */
(() => {
  'use strict';
  window.AMCCommerceRequest = async (url, options = {}) => {
    const controller = new AbortController();
    const upstream = options.signal;
    let timer, cancel;
    const stopped = new Promise((_, reject) => {
      cancel = () => {
        controller.abort();
        reject(new DOMException('Request cancelled.', 'AbortError'));
      };
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('The request timed out. Reload the cart to review its current state.'));
      }, 12000);
      if (upstream?.aborted) cancel();
      else upstream?.addEventListener('abort', cancel, {once:true});
    });
    try {
      return await Promise.race([
        stopped,
        (async () => {
          if (controller.signal.aborted) throw new DOMException('Request cancelled.', 'AbortError');
          const response = await fetch(url, {...options, signal:controller.signal});
          const text = await response.text();
          return {response, text};
        })()
      ]);
    } finally {
      clearTimeout(timer);
      upstream?.removeEventListener('abort', cancel);
    }
  };
})();

/* Configuration integrity for Shopify cart line items; no network or cart mutation. */
(() => {
  'use strict';
  const groupId = item => String(item.properties?.['_AMC configuration'] || '');
  const fail = message => Object.assign(new Error(message), {code:'AMC_CART_INTEGRITY'});
  const positive = value => Number.isSafeInteger(value) && value > 0;
  function inspect(items) {
    if (!Array.isArray(items)) throw fail('The cart could not be checked. Refresh the cart before continuing.');
    const groups = new Map(), keys = new Set();
    for (const item of items) {
      if (!item || typeof item.key !== 'string' || !item.key || keys.has(item.key) || !positive(item.quantity)) throw fail('The cart contains an invalid or duplicate line. Refresh the cart before continuing.');
      keys.add(item.key);
      const id = groupId(item);
      if (id) {if (!groups.has(id)) groups.set(id, []);groups.get(id).push(item);}
    }
    const results = [];
    for (const [id, lines] of groups) {
      const raw = lines.map(line => line.properties?.['_AMC build']);
      if (raw.every(value => value === undefined || value === null || value === '')) throw fail('This build was added before complete-build verification. Remove it and configure it again before checkout.');
      if (raw.some(value => typeof value !== 'string' || !value) || raw.some(value => value !== raw[0])) throw fail('This configured build has inconsistent component records. Review the complete build before checkout.');
      let manifest;
      try {manifest = JSON.parse(raw[0]);} catch (_) {throw fail('This configured build has an unreadable component record. Review the build before checkout.');}
      if (!manifest || manifest.version !== 1 || !Array.isArray(manifest.items) || !manifest.items.length) throw fail('This configured build has no valid component list.');
      const expected = new Map();
      for (const component of manifest.items) {
        const variant = String(component?.id);
        if (!/^\d+$/.test(variant) || !positive(Number(variant)) || !positive(component.ratio) || !Number.isSafeInteger(component.price) || component.price < 0 || expected.has(variant)) throw fail('This configured build has an invalid component record.');
        expected.set(variant, component);
      }
      if (lines.length !== expected.size) throw fail('A configured build is missing a component or contains an extra item. Restore the complete build before checkout.');
      const seen = new Set();
      let reference;
      for (const line of lines) {
        const variant = String(line.variant_id ?? line.id), component = expected.get(variant);
        if (!component || seen.has(variant)) throw fail('A configured build has duplicate or unexpected components.');
        if (line.quantity % component.ratio !== 0) throw fail('Configured quantities must contain a whole number of complete builds.');
        if (line.original_price !== component.price) throw fail('A configured component price has changed. Review the build before checkout.');
        if (reference && BigInt(line.quantity) * BigInt(reference.original) !== BigInt(reference.current) * BigInt(component.ratio)) throw fail('Configured component quantities no longer match. Restore the complete build before checkout.');
        reference ||= {original:component.ratio, current:line.quantity};
        seen.add(variant);
      }
      results.push({id, legacy:false, verified:true});
    }
    return {valid:true, message:'', groups:results, legacyGroups:results.filter(group => group.legacy).map(group => group.id)};
  }
  function validate(items) {
    try {return inspect(items);} catch (error) {return {valid:false, groups:[], legacyGroups:[], message:error.message, error:error.message};}
  }
  function updates(items, lineKey, newQty) {
    if (!Array.isArray(items) || !Number.isSafeInteger(newQty) || newQty < 0) throw fail('Enter a whole-number quantity of zero or more.');
    const matches = items.filter(item => item?.key === lineKey);
    if (matches.length !== 1) throw fail('The selected cart line could not be identified. Refresh the cart.');
    const selected = matches[0], id = groupId(selected);
    if (!id) return {[lineKey]:newQty};
    const lines = items.filter(item => groupId(item) === id);
    // Removal remains possible even when a build is already incomplete.
    if (newQty === 0) return Object.fromEntries(lines.map(line => [line.key, 0]));
    inspect(lines);
    const rawManifest = selected.properties?.['_AMC build'];
    if (rawManifest) {
      const component = JSON.parse(rawManifest).items.find(item => String(item.id) === String(selected.variant_id ?? selected.id));
      if (!component || newQty % component.ratio !== 0) throw fail('Choose a quantity that keeps complete configured builds together.');
    }
    if (!positive(selected.quantity)) throw fail('The configured quantity could not be checked.');
    const result = {};
    for (const line of lines) {
      const numerator = BigInt(line.quantity) * BigInt(newQty), denominator = BigInt(selected.quantity);
      if (numerator % denominator !== 0n) throw fail('This quantity would split a configured build. Choose a quantity that keeps all components together.');
      const quantity = Number(numerator / denominator);
      if (!positive(quantity)) throw fail('The configured quantity is outside the supported range.');
      result[line.key] = quantity;
    }
    return result;
  }
  window.AMCConfiguredCart = Object.freeze({validate, updates});
})();

// Standard theme checkout waits for a fresh, complete configuration check.
(() => {
  let pending = false;
  let invalid = false;
  let permittedForm = null;
  const hasBuild = () => !!document.querySelector('[data-amc-configuration]');
  const controls = () => document.querySelectorAll('button[name="checkout"]');
  const ui = window.AMCBuildCartUI = {
    uncertain:false,
    get busy() {return pending || this.uncertain;},
    async read() {
      const root = new URL(window.Shopify?.routes?.root || '/',location.origin);
      if(root.origin !== location.origin) throw new Error('The cart route is invalid.');
      if(!root.pathname.endsWith('/')) root.pathname += '/';
      const {response, text} = await window.AMCCommerceRequest(new URL('cart.js',root).href,{credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json'}});
      if(!response.ok) throw new Error('Your cart could not be checked.');
      const cart = JSON.parse(text);
      if(!Array.isArray(cart.items)) throw new Error('Your cart could not be checked.');
      return cart;
    },
    setBusy(value) {
      pending=value;
      controls().forEach(button=>{
        if(button.dataset.amcOriginalDisabled === undefined) button.dataset.amcOriginalDisabled=String(button.disabled && button.dataset.amcCheckoutAwaiting !== 'true');
        button.disabled=pending || invalid || this.uncertain || button.dataset.amcOriginalDisabled==='true';
      });
      document.querySelectorAll('.additional-checkout-buttons,shopify-accelerated-checkout-cart').forEach(node=>{
        if(hasBuild() || pending || this.uncertain) {node.dataset.amcHiddenCheckout='true';node.hidden=true;node.style.setProperty('display','none','important');}
        else if(node.dataset.amcHiddenCheckout) {node.hidden=false;node.style.removeProperty('display');delete node.dataset.amcHiddenCheckout;}
      });
    },
    error(message) {
      document.querySelectorAll('cart-items,cart-drawer-items').forEach(surface=>{
        let node=surface.querySelector('[data-amc-cart-status]');
        if(!node) {node=document.createElement('p');node.dataset.amcCartStatus='true';node.setAttribute('role','alert');surface.prepend(node);}
        node.textContent=message;
        if(message) {const link=document.createElement('a');link.href=(window.Shopify?.routes?.root || '/')+'cart';link.textContent=' Reload cart';node.append(link);}
        node.hidden=!message;
      });
    },
    confirmValid() {invalid=false;this.error('');},
    async verify() {
      const cart=await this.read();
      const result=window.AMCConfiguredCart.validate(cart.items);
      invalid=!result.valid;
      if(invalid) throw new Error(result.message+' Remove the affected build and configure it again.');
      this.error('');
      return cart;
    }
  };
  async function checkout(form,button) {
    if(ui.busy) return;
    ui.setBusy(true);
    try {
      await ui.verify();
      ui.setBusy(false);
      permittedForm=form;
      form.requestSubmit(button);
      permittedForm=null;
    } catch(error) {
      invalid=true;
      ui.error(error.message || 'Review your configured build before checkout.');
    } finally {ui.setBusy(false);}
  }
  document.addEventListener('click',event=>{
    const button=event.target.closest?.('button[name="checkout"]');
    if(!button || (!hasBuild() && !ui.busy)) return;
    event.preventDefault();event.stopImmediatePropagation();
    if(ui.busy) {ui.error(ui.uncertain ? 'The previous cart update is unconfirmed. Reload the cart before checkout.' : 'Please wait for the cart check to finish.');return;}
    if(button.form) checkout(button.form,button);
  },true);
  document.addEventListener('submit',event=>{
    if(event.target===permittedForm) {permittedForm=null;return;}
    if(!['cart','CartDrawer-Form'].includes(event.target.id) || (!hasBuild() && !ui.busy)) return;
    // Native form submission can submit individual quantities without the group handler.
    event.preventDefault();event.stopImmediatePropagation();
    if(ui.busy) {ui.error(ui.uncertain ? 'The previous cart update is unconfirmed. Reload the cart before checkout.' : 'Please wait for the cart check to finish.');return;}
    if(event.submitter?.name==='checkout') checkout(event.target,event.submitter);
    else ui.error('Use the quantity controls to update the entire build before checkout.');
  },true);
  function start() {
    if(!hasBuild()) return;
    ui.setBusy(true);
    ui.verify().catch(error=>{invalid=true;ui.error(error.message);}).finally(()=>ui.setBusy(false));
  }
  function initialize() {
    const signature = () => [...document.querySelectorAll('[data-amc-configuration]')].map(node=>node.dataset.amcCartKey).join('|');
    let previous = signature();
    const observer = new MutationObserver(() => {
      const next=signature();
      if(next===previous) return;
      previous=next;
      ui.setBusy(pending);
      if(hasBuild() && !ui.busy) start();
    });
    document.querySelectorAll('cart-drawer,#main-cart').forEach(node=>observer.observe(node,{childList:true,subtree:true}));
    start();
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',initialize,{once:true});else initialize();
})();
